import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, ChartColumnBig, IndianRupee } from 'lucide-react';
import { Card, CardContent, Typography, Dialog, DialogContent, Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";

import { setSelectedItem, clearSelectedItem, clearMarketData, fetchMarketDataSuccess } from '../../store/reducers/marketReducer';
import { setOpenDialog, setOpenPopupDialog, setMessage, clearMessage } from '../../store/reducers/uiReducer';
import { toIndianRupeeFormat } from '../utils/helpers';

import MarketTabs from './MarketTabs';
import MarketCard from './MarketCard';
import PasswordChangeDialog from './PasswordChangeDialog';
import ShimmerCard from './ShimmerCard';
import Footer from '../Footer';
import { getClientData } from '../custom';
import MarketDetailDialog from '../MarketDetailDialog';

const Market = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab, activeTabList, symbolTokens, displayedCards, selectedItem, loading } = useSelector(state => state.market);


  const { openDialog, openPopupDialog, message } = useSelector(state => state.ui);

  const [clients, setClients] = React.useState([]);
  const [formattedDate, setFormattedDate] = React.useState();

  useEffect(() => {
    const cached = localStorage.getItem("marketData");

    if (cached) {
      try {
        dispatch(fetchMarketDataSuccess(JSON.parse(cached)));
      } catch (e) {
        console.error("Failed to parse cached marketData", e);
      }
    }

    // Start live updates
    dispatch({ type: 'START_MARKET_DATA_UPDATES' });

    return () => {
      dispatch({ type: 'STOP_MARKET_DATA_UPDATES' });
    };
  }, [dispatch]);

  if (openDialog && selectedItem) {
    const updatedItem = displayedCards.find((item) => item.tradingSymbol === selectedItem.tradingSymbol);
    if (updatedItem) {
      dispatch(setSelectedItem(updatedItem));
    }
  }
   let sortedBuy = [];
  let sortedSell = [];

  if (selectedItem) {
    sortedBuy = [...(selectedItem?.depth?.buy || [])].sort((a, b) => b.price - a.price) ?? [];
    sortedSell = [...(selectedItem?.depth?.sell || [])].sort((a, b) => a.price - b.price);
  }

  useEffect(() => {

    const parsedUserData = getClientData();
    if (parsedUserData) {
      setClients(parsedUserData);
    }

    // Handle popup logic
    const hasSeenPopup = localStorage.getItem('hasSeenMarketPopup');
    const loginMessage = location.state?.loginMessage;

    if (parsedUserData?.is_password !== '1') {
      if (!hasSeenPopup) {
        dispatch(setOpenPopupDialog(true));
        localStorage.setItem('hasSeenMarketPopup', 'true');
      } else {
        dispatch(setOpenPopupDialog(true));
      }
    }

    // Handle login message
    const hasShownLoginMessage = sessionStorage.getItem('hasShownLoginMessage');
    if (loginMessage && !hasShownLoginMessage) {
      dispatch(setMessage(loginMessage));
      sessionStorage.setItem('hasShownLoginMessage', 'true');

      const timer = setTimeout(() => {
        dispatch(clearMessage());
        navigate(location.pathname, { replace: true });
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [dispatch, location, navigate]);

  const handleDialogOpen = (card, formattedDate) => {

    dispatch(setSelectedItem(card));
    setFormattedDate(formattedDate);
    dispatch(setOpenDialog(true));
  };

  const handleDialogClose = () => {
    dispatch(setOpenDialog(false));
    dispatch(clearSelectedItem());
  };

  const handleRedirect = (buttonType, tradingSymbol, exchange, price, lots, netChange, percentChange, symbolToken, lowerCircuit, upperCircuit, high, low, script_id, exchange_id, formattedDate) => {

    navigate('/add-order', {
      state: {
        m_buttonType: buttonType,
        m_scriptName: tradingSymbol,
        m_exchange: exchange,
        m_price: price,
        m_lotssize: 1,
        m_qty: lots,
        m_netChange: netChange,
        m_percentChange: percentChange,
        m_symbolToken: symbolToken,
        m_activeTabValue: activeTab,
        m_expiry: formattedDate,
        m_lowerCircuit: lowerCircuit,
        m_upperCircuit: upperCircuit,
        m_high: high,
        m_low: low,
        m_script_id: script_id,
        m_exchange_id: exchange_id
      },
    });
    dispatch(setOpenDialog(false));

  };

  const handleSearchBar = () => {
    navigate("/search-bar");
  };

  const initials = (clients.first_name?.charAt(0).toUpperCase() || '') +
    (clients.last_name?.charAt(0).toUpperCase() || '');

  return (
    <>
      {message && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            zIndex: 9999,
            backgroundColor: "#d4edda",
            color: "#155724",
            padding: "12px 20px",
            border: "1px solid #c3e6cb",
            borderRadius: "5px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            minWidth: "250px",
            maxWidth: "400px"
          }}
        >
          <div>
            <strong>{message}</strong>
          </div>
          <button
            onClick={() => dispatch(clearMessage())}
            style={{
              background: "transparent",
              border: "none",
              color: "#155724",
              fontSize: "18px",
              marginLeft: "15px",
              cursor: "pointer"
            }}
            aria-label="Close alert"
          >
            &times;
          </button>
        </div>
      )}

      <div className="flex items-center justify-center flex-col bg-black min-h-screen p-0">
        <div className="h-[100vh] overflow-y-hidden p-4 pb-20 flex-grow max-w-sm min-w-[375px] w-full bg-gray-100 
            [&::-webkit-scrollbar]:w-1 
            [&::-webkit-scrollbar-track]:bg-transparent 
            [&::-webkit-scrollbar-thumb]:bg-transparent 
            scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">

          <div className="flex items-center justify-between w-full">
            <div className="flex items-center">
              <Search onClick={handleSearchBar} className="cursor-pointer" />
            </div>
            <div className="flex items-center justify-end">
              <div className="mr-2">
                <h3 className="text-[1rem] font-lato">Hi {clients.first_name}</h3>
              </div>
              <div className="relative bg-pink-100 text-red-800 rounded-full w-10 h-10 flex items-center justify-center shadow-md">
                <span className="text-xs font-lato font-semibold">{initials}</span>
              </div>
            </div>
          </div>

          <div className="mt-2">
            <Card className="rounded-xl px-4 bg-blue-500 h-30">
              <CardContent>
                <div className="flex items-center justify-between">
                  <Typography variant="h5" component="div" className="font-lato text-sm text-white">
                    My Portfolio
                  </Typography>
                  <ChartColumnBig className="flex items-center w-5 h-5 text-white" />
                </div>
                <div className="flex items-center justify-between">
                  <Typography variant="body2" className="mt-1 font-lato text-white text-lg font-semibold">
                    <span className='flex items-center'>
                      {/* <IndianRupee size={18} /> */}
                      {toIndianRupeeFormat(clients?.client_account?.balance)}
                    </span>
                  </Typography>
                  <Typography variant="h5" component="div" className="font-lato text-sm text-white">
                    +2.5%
                  </Typography>
                </div>
              </CardContent>
            </Card>
          </div>

          <MarketTabs />

          <div className="flex justify-center space-x-24 pt-3 w-full h-auto">
            <span className="font-lato text-[14px] text-black">NAME</span>
            <span className="font-lato text-[14px] text-black">VALUE</span>
            <span className="font-lato text-[14px] text-black">CHG (%)</span>
          </div>

          {/* Card List */}
          <div className="overflow-y-auto h-[100vh]
                        [&::-webkit-scrollbar]:w-1 
                        [&::-webkit-scrollbar-track]:bg-transparent 
                        [&::-webkit-scrollbar-thumb]:bg-transparent 
                        scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent">
            {
              (() => {
                // Only show cards that actually have a name. This drops the
                // empty "0.00 / Vol 0" rows for dead tokens that never get a
                // tick, so the list looks clean.
                const visibleCards = (displayedCards || []).filter(
                  (c) => c && c.tradingSymbol
                );
                if (loading || visibleCards.length === 0) {
                  return Array.from({ length: 10 }).map((_, index) => (
                    <ShimmerCard key={index} />
                  ));
                }
                return visibleCards.slice(0, 10).map((card, index) => (
                  <MarketCard
                    key={card.symbolToken || index}
                    card={card}
                    onCardClick={handleDialogOpen}
                  />
                ));
              })()
            }

            {/* Market Detail Dialog */}
            <MarketDetailDialog
              open={openDialog}
              onClose={handleDialogClose}
              selectedItem={selectedItem}
              formattedDate={formattedDate}
              handleRedirect={handleRedirect}
            />

            <PasswordChangeDialog />
          </div>
        </div>

        <Footer />
        <ToastContainer />
      </div>
    </>
  );
};

export default Market;